import { Injectable, inject } from '@angular/core';
import { TosItem } from '@bepatient/common';
import { CoreHttpClient, CoreLogin, selectorCoreLogin } from '@bepatient/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnboardingApiService } from './onboarding-api.service';
import {
  Onboarding,
  OnboardingStepConclusion,
  OnboardingStepIntro,
  OnboardingStepPassword,
  OnboardingSteps,
  OnboardingStepTOS,
  OnboardingStepTypes,
  BirthdateFormat
} from './onboarding.models';

@Injectable()
export class OnboardingApiSmartService implements OnboardingApiService {
  private http = inject(CoreHttpClient);
  private store = inject<Store<any>>(Store);

  changePassword(
    email: string,
    birthdate?: string,
    deviceType?: string,
    appVersion?: string
  ): Observable<any> {
    return this.http.post<any>('/rest/users/reset_password/authorize', {
      email,
      birthdate,
      device_type: deviceType,
      app_version: appVersion
    });
  }

  passwordReset(
    data: any,
    deviceType?: string,
    appVersion?: string
  ): Observable<Partial<CoreLogin>> {
    return this.http
      .post<any>(
        '/rest/users/reset_password',
        {
          new_password: data.newPassword,
          confirm_password: data.newPassword,
          device_type: deviceType,
          app_version: appVersion
        },
        { withMobiCredentials: true }
      )
      .pipe(
        map(res => ({ iAuth: res.iauth || '', message: res.message || '' }))
      );
  }

  authorize(
    email: string,
    birthdate?: string,
    fcmToken?: string,
    deviceType?: string,
    userTimezone?: string,
    appVersion?: string
  ): Observable<Partial<CoreLogin>> {
    return this.http
      .post<any>('/rest/onboarding/authorize', {
        email,
        birthdate,
        fcm_token: fcmToken,
        device_type: deviceType,
        user_timezone: userTimezone,
        app_version: appVersion
      })
      .pipe(
        map(res => ({
          iAuth: res.iauth || '',
          userId: (res.user_id && parseInt(res.user_id, 10)) || 0,
          language: res.user_language || '',
          tokenValidationCheckFrequency:
            res.token_validation_check_frequency || void 0,
          tokenWarningExpirationDuration:
            res.token_warning_expiration_duration || void 0,
          rightToLeft: res.right_to_left || false
        }))
      );
  }

  loadOnboarding(): Observable<Onboarding> {
    return this.http
      .get<any>('/rest/onboarding', { withMobiCredentials: true })
      .pipe(
        map(res => {
          const steps = { steps: res.payload };
          return this.SmartOnboardingParser(steps);
        })
      );
  }

  loadOnboardingTosDetails(url): Observable<any> {
    return this.http
      .get<any>(url, { withMobiCredentials: true })
      .pipe(map(res => res.payload));
  }

  onboardingSubmitStep(
    data: any,
    deviceType?: string,
    appVersion?: string
  ): Observable<Partial<CoreLogin>> {
    return this.http
      .put<any>(
        '/rest/onboarding',
        {
          ...data,
          device_type: deviceType,
          app_version: appVersion
        },
        {
          withMobiCredentials: true
        }
      )
      .pipe(
        map(res =>
          res.iauth
            ? {
                iAuth: res.iauth,
                tokenValidationCheckFrequency:
                  res.token_validation_check_frequency,
                tokenWarningExpirationDuration:
                  res.token_warning_expiration_duration,
                smart: true,
                isNewDevice: res.is_new_device || void 0,
                popupNewDeviceMessage: res.popup_new_device_message || void 0,
                isDisplayQuickTour: res.is_display_quicktour || false
              }
            : {}
        )
      );
  }

  onboardingFinalize(): Observable<Partial<CoreLogin>> {
    let coreLogin = this.store.pipe(select(selectorCoreLogin));
    coreLogin.subscribe((data: any) => {
      coreLogin = data;
    });
    return coreLogin;
  }

  public getBirthdateFormat(apiDateFormat: string): BirthdateFormat {
    switch (apiDateFormat) {
      case 'MM-YYYY':
      case 'MM/YYYY':
      case 'mm/yyyy':
        return BirthdateFormat.YEAR_MONTH_FR;
      case 'YYYY-MM':
      case 'YYYY/MM':
      case 'yyyy/mm':
        return BirthdateFormat.YEAR_MONTH;

      case 'DD-MM-YYYY':
      case 'DD/MM/YYYY':
      case 'dd/mm/yyyy':
        return BirthdateFormat.YEAR_MONTH_DAY_FR;

      case 'YYYY-MM-DD':
      case 'YYYY/MM/DD':
      case 'yyyy/mm/dd':
        return BirthdateFormat.YEAR_MONTH_DAY;

      default:
        return BirthdateFormat.YEAR;
    }
  }

  private SmartOnboardingParser(data: Onboarding): Onboarding {
    const stepType = (item: any): OnboardingStepTypes => {
      if (item.type === 'onboarding-intro') {
        return OnboardingStepTypes.INTRO;
      } else if (item.type === 'onboarding-password') {
        return OnboardingStepTypes.PASSWORD;
      } else if (item.type === 'onboarding-tos') {
        return OnboardingStepTypes.TOS;
      } else {
        return OnboardingStepTypes.CONCLUSION;
      }
    };

    // Arranging onboarding steps in order [ Introduction, Password, Conclusion ]
    const arrangeSteps = (ulSteps: any): OnboardingSteps[] => {
      for (let i = 0; i < ulSteps.length; i++) {
        if (ulSteps[i].type === OnboardingStepTypes.INTRO) {
          // always add onboarding-intro in the begning
          const first = ulSteps.splice(i, 1);
          ulSteps.unshift(first[0]);
          break;
        } else if (ulSteps[i].type === OnboardingStepTypes.CONCLUSION) {
          // always add onboarding-conclusion in the last
          const last = ulSteps.splice(i, 1);
          ulSteps.push(last[0]);
          break;
        } else {
          return ulSteps;
        }
      }
      return ulSteps;
    };

    const introParser = (step: any): OnboardingStepIntro => ({
      title: step.title,
      type: OnboardingStepTypes.INTRO,
      color: step.color,
      instanceLogo: step.instanceLogo || void 0,
      content: step.content,
      buttonLabel: step.button_label
    });

    const tosParser = (step: any): OnboardingStepTOS => ({
      type: OnboardingStepTypes.TOS,
      stepUuid: step.id,
      color: step.color,
      title: step.title,
      uuid: step.type,
      buttonLabel: step.button_label,
      subTitle: step.sub_title,
      items: step.items.map(
        item =>
          <TosItem>{
            uuid: item.field_name,
            title: item.title,
            excerpt: item.abstract,
            content: item.button_link,
            required: item.required,
            toggleText: item.toggle_text,
            buttonLabel: item.button_label,
            videoType: item.video_type,
            type: item.video,
            url: item.url
          }
      )
    });

    const passwordParser = (step: any): OnboardingStepPassword => ({
      title: step.title,
      type: OnboardingStepTypes.PASSWORD,
      color: step.color,
      subTitle: step.subtitle,
      uuid: step.type,
      stepUuid: step.type,
      buttonLabel: step.button_label,
      fieldLabel: step.field_label,
      passwordPolicy: step.password_policy
    });

    const conclusionParser = (step: any): OnboardingStepConclusion => ({
      title: step.title,
      type: OnboardingStepTypes.CONCLUSION,
      color: step.color,
      image: step.image,
      instanceLogo: step.instanceLogo || void 0,
      content: step.content,
      buttonLabel: step.button_label
    });

    const steps: Array<OnboardingSteps> = data.steps.map(step => {
      switch (stepType(step)) {
        case OnboardingStepTypes.INTRO:
          return introParser(step);
        case OnboardingStepTypes.TOS:
          return tosParser(step);
        case OnboardingStepTypes.PASSWORD:
          return passwordParser(step);
        case OnboardingStepTypes.CONCLUSION:
          return conclusionParser(step);
      }
    });

    // arrangeSteps(steps);
    return { steps };
  }
}
